import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAppDispatch, useAppSelector } from '@app/hooks/index';
import {
  Button,
  Separator,
  Title,
  TreeCheckbox,
  CHECKBOX_STATES,
  CheckboxContainer,
} from '@common/components';
import { useGetAllProjectsQuery } from '@dashboard/api/projects';
import { useGetAllCountriesQuery } from '@dashboard/api/countries';
import { setProjects, selectProjects } from '@dashboard/slices/projects';
import { setCountries, selectCountries } from '@dashboard/slices/countries';
import {
  selectFilter,
  updateFilters,
  tmpFilter,
  setFilter,
  updateTmpFilter,
  setTmpFilters,
  selectOptions,
  updateOptions,
  applyFilters,
} from '@dashboard/slices/filters';
import { setErrorModal } from '@app/slices/modals';
import { useTranslation } from 'react-i18next';
import { Permission } from '@app/components/Permission';
import { PERMISSIONS } from '@app/constants/permissions';
import filterClasses from './MainFilters.module.scss';

export default function MainFilters(props) {
  const { children } = props;
  const { t, i18n } = useTranslation('dashboard\\MainFilters');

  const projectsQuery = useGetAllProjectsQuery({ refetchOnFocus: true });

  const countriesQuery = useGetAllCountriesQuery({ refetchOnFocus: true });

  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const currentFilters = useAppSelector(selectFilter);
  const tmpFilters = useAppSelector(tmpFilter);

  const [allProjectsSelected, setAllProjectsSelected] = useState(true);
  const [allCountriesSelected, setAllCountriesSelected] = useState(true);

  const [checkedTreeState, setCheckedTreeState] = useState({});

  const countries = useAppSelector(selectCountries);
  const filterOptions = useAppSelector(selectOptions);

  const handleApplyFilters = () => {
    if (tmpFilters.projects.length <= 0) {
      dispatch(
        setErrorModal({
          data: { message: t('filterProjectValidation') },
        }),
      );
    } else if (tmpFilters.centers.length <= 0) {
      dispatch(
        setErrorModal({
          data: { message: t('filterCenterValidation') },
        }),
      );
    } else {
      Promise.all([dispatch(setFilter(tmpFilters)), dispatch(applyFilters())]);
    }
  };

  const changeSelected = (add, key, items) => {
    const itemIds = items.map((item) => item.uuid);
    if (add) {
      dispatch(
        updateTmpFilter({
          [key]: Array.from(new Set([...tmpFilters[key], ...itemIds])),
        }),
      );
    } else {
      dispatch(
        updateTmpFilter({
          [key]: tmpFilters[key].filter((f) => !itemIds.includes(f)),
        }),
      );
    }

    if (key === 'projects') {
      const centersToAddOrRemove = [];
      countries.forEach((country) => {
        country.centers.forEach((center) => {
          if (
            itemIds.includes(center.project_id) &&
            ((add && !tmpFilters.centers.includes(center.uuid)) ||
              (!add && tmpFilters.centers.includes(center.uuid)))
          ) {
            centersToAddOrRemove.push(center);
          }
        });
      });

      if (centersToAddOrRemove.length) {
        changeSelected(add, 'centers', centersToAddOrRemove);
      }
    }

    if (key === 'countries') {
      let centersToAddOrRemove;

      items.forEach((country) => {
        if (add) {
          centersToAddOrRemove = country.centers.filter(
            (center) => !tmpFilters.centers.includes(center.uuid),
          );
        } else {
          centersToAddOrRemove = country.centers.filter((center) =>
            tmpFilters.centers.includes(center.uuid),
          );
        }
      });

      if (centersToAddOrRemove.length) {
        changeSelected(add, 'centers', centersToAddOrRemove);
      }
    }

    if (key === 'centers') {
      if (add) {
        const countryIdsToAdd = items.map((center) => center.country_id);
        const projectIdsToAdd = items.map((center) => center.project_id);

        dispatch(
          updateTmpFilter({
            countries: Array.from(
              new Set([...tmpFilters.countries, ...countryIdsToAdd]),
            ),
            projects: Array.from(
              new Set([...tmpFilters.projects, ...projectIdsToAdd]),
            ),
          }),
        );
      } else {
        const currentTmpFilterCenters = tmpFilters.centers.filter(
          (c) => !itemIds.includes(c),
        );

        const allCenters = [];
        countries.forEach((country) => {
          allCenters.push(...country.centers);
        });

        const projectIds = new Set(items.map((center) => center.project_id));
        const projectIdsToRemove = [];
        projectIds.forEach((projectId) => {
          const projectHasSelectedCenters = allCenters.some(
            (center) =>
              center.project_id === projectId &&
              currentTmpFilterCenters.includes(center.uuid),
          );

          if (!projectHasSelectedCenters) projectIdsToRemove.push(projectId);
        });

        const countryIds = new Set(items.map((center) => center.country_id));
        const countryIdsToRemove = [];
        countryIds.forEach((countryId) => {
          const countryHasSelectedCenters = allCenters.some(
            (center) =>
              center.country_id === countryId &&
              currentTmpFilterCenters.includes(center.uuid),
          );

          if (!countryHasSelectedCenters) countryIdsToRemove.push(countryId);
        });
        let { projects: tmpFiltersProjects, countries: tmpFiltersCountries } =
          tmpFilters;

        if (projectIdsToRemove.length) {
          tmpFiltersProjects = tmpFiltersProjects.filter(
            (p) => !projectIdsToRemove.includes(p),
          );
        }

        if (countryIdsToRemove.length) {
          tmpFiltersCountries = tmpFiltersCountries.filter(
            (c) => !countryIdsToRemove.includes(c),
          );
        }

        if (projectIdsToRemove.length || countryIdsToRemove.length) {
          dispatch(
            updateTmpFilter({
              projects: tmpFiltersProjects,
              countries: tmpFiltersCountries,
            }),
          );
        }
      }
    }
  };

  const toggleSelectAllCountries = (forceState) => {
    if (countries && countries.length > 0) {
      const newSelection = forceState || !allCountriesSelected;
      setAllCountriesSelected(newSelection);
      setAllProjectsSelected(newSelection);

      if (newSelection) {
        let centers = [];
        countries.forEach((country) => {
          centers = [
            ...centers,
            ...country.centers.map((center) => center.uuid),
          ];
        });

        dispatch(
          updateTmpFilter({
            projects: projects.map((p) => p.uuid),
            countries: countries.map((p) => p.uuid),
            centers,
          }),
        );
      } else {
        dispatch(updateTmpFilter({ countries: [], centers: [], projects: [] }));
      }
    }
  };

  const toggleSelectAllProjects = (forceState) => {
    if (projects && projects.length > 0) {
      const newSelection = forceState || !allProjectsSelected;
      setAllProjectsSelected(newSelection);
      setAllCountriesSelected(newSelection);

      if (newSelection) {
        const allCenterIds = [];
        countries.forEach((c) => {
          allCenterIds.push(...c.centers.map((center) => center.uuid));
        });

        dispatch(
          updateTmpFilter({
            projects: projects.map((p) => p.uuid),
            countries: countries.map((c) => c.uuid),
            centers: allCenterIds,
          }),
        );
      } else {
        dispatch(
          updateTmpFilter({
            projects: [],
            countries: [],
            centers: [],
          }),
        );
      }
    }
  };

  const handleChangeSelectedCenterOrCountry = (status, item) => {
    if (status === CHECKBOX_STATES.Empty) {
      if (item.code) {
        changeSelected(false, 'countries', [item]);
      } else if (item.name) {
        changeSelected(false, 'centers', [item]);
      }
    } else if (status === CHECKBOX_STATES.Checked) {
      if (item.code) {
        changeSelected(true, 'countries', [item]);
      } else if (item.name) {
        changeSelected(true, 'centers', [item]);
      }
    }
  };
  const sortCountries = (newCountries) => {
    const sortedCountries = [...newCountries]
      .map((cx) => ({
        ...cx,
        centers: [...cx.centers].sort((c1, c2) =>
          t(c1.name).localeCompare(t(c2.name)),
        ),
      }))
      .sort((c1, c2) => t(c1.code).localeCompare(t(c2.code)));

    return sortedCountries;
  };

  const fnSortCountries = () => {
    if (countries && countries.length > 0)
      dispatch(setCountries(sortCountries(countries)));
  };

  useEffect(() => {
    i18n.on('languageChanged', fnSortCountries);
    i18n.on('loaded', fnSortCountries);

    return () => {
      i18n.off('languageChanged', fnSortCountries);
      i18n.off('loaded', fnSortCountries);
    };
  }, []);

  useEffect(() => {
    if (projectsQuery.isSuccess) dispatch(setProjects(projectsQuery.data));
    if (countriesQuery.isSuccess) {
      dispatch(
        setCountries(
          countriesQuery.data &&
            countriesQuery.data.data &&
            countriesQuery.data.data.length
            ? sortCountries(countriesQuery.data.data)
            : null,
        ),
      );
    }
  }, [projectsQuery, countriesQuery]);

  const refreshFilterState = (currentFiltersSelected) => {
    const selectedItems = {};
    projects.forEach((project) => {
      selectedItems[project.uuid] =
        currentFiltersSelected.projects.indexOf(project.uuid) > -1
          ? CHECKBOX_STATES.Checked
          : CHECKBOX_STATES.Empty;
    });
    countries.forEach((country) => {
      let checkedCount = 0;
      country.centers.forEach((center) => {
        const isChecked =
          currentFiltersSelected.centers.indexOf(center.uuid) > -1;
        if (isChecked) checkedCount += 1;
        selectedItems[center.uuid] = isChecked
          ? CHECKBOX_STATES.Checked
          : CHECKBOX_STATES.Empty;
      });

      if (checkedCount >= 1) {
        if (checkedCount < country.centers.length) {
          selectedItems[country.uuid] = CHECKBOX_STATES.Indeterminate;
        } else {
          selectedItems[country.uuid] = CHECKBOX_STATES.Checked;
        }
      } else {
        selectedItems[country.uuid] = CHECKBOX_STATES.Empty;
      }
    });

    setCheckedTreeState(selectedItems);
  };

  useEffect(() => {
    if (filterOptions.isFirstLoad === true && projects && countries) {
      toggleSelectAllProjects(true);
      toggleSelectAllCountries(true);
    }
  }, [projects, countries]);

  useEffect(() => {
    if (!filterOptions.isFirstLoad) {
      dispatch(setTmpFilters(currentFilters));
      refreshFilterState(currentFilters);

      if (currentFilters.projects.length === projects.length) {
        setAllProjectsSelected(true);
      } else if (currentFilters.projects.length === 0 && projects.length > 0) {
        setAllProjectsSelected(false);
      }

      if (currentFilters.countries.length === countries.length) {
        setAllCountriesSelected(true);
      } else if (
        currentFilters.countries.length === 0 &&
        countries.length > 0
      ) {
        setAllCountriesSelected(false);
      }
    }
  }, [currentFilters]);

  useEffect(() => {
    if (!filterOptions.isFirstLoad) {
      refreshFilterState(tmpFilters);
    } else if (
      tmpFilters.projects.length > 0 &&
      tmpFilters.centers.length > 0
    ) {
      dispatch(
        updateFilters({
          projects: tmpFilters.projects,
          countries: tmpFilters.countries,
          centers: tmpFilters.centers,
        }),
      );

      dispatch(updateOptions({ isFirstLoad: false }));
    }
  }, [tmpFilters]);

  return (
    <div className={filterClasses.main}>
      <div className={filterClasses.container}>
        <div className={filterClasses.titleContainer}>
          <Title type="h1" color="font-primary" className={filterClasses.title}>
            {t('titleFilterCenters')}
          </Title>
        </div>

        <div>
          <Permission permission={PERMISSIONS.VIEW.projectsList}>
            <div>
              <div className={filterClasses.subtitleContainer}>
                <Title
                  type="h2"
                  color="secondary"
                  className={filterClasses.subtitle}
                >
                  {t('titleFilterByProject')}
                </Title>
                <Button
                  color="accent"
                  onClick={() => toggleSelectAllProjects()}
                  pointer
                >
                  {!allProjectsSelected
                    ? t('buttonSelectAllLabel')
                    : t('buttonDeselectAllLabel')}
                </Button>
              </div>
              <Separator />
              <div className={filterClasses.body}>
                {projects &&
                  projects.map((project) => {
                    return (
                      <CheckboxContainer
                        key={project.uuid}
                        id={project.uuid}
                        value={project.uuid}
                        className={filterClasses.reducePadding}
                        onChange={(e) =>
                          changeSelected(e.target.checked, 'projects', [
                            project,
                          ])
                        }
                        label={project.name}
                        checked={
                          tmpFilters.projects.indexOf(project.uuid) > -1
                            ? CHECKBOX_STATES.Checked
                            : CHECKBOX_STATES.Empty
                        }
                        isIndeterminate={false}
                      />
                    );
                  })}
              </div>
            </div>
          </Permission>
        </div>

        <div className={filterClasses.subtitleContainer}>
          <Title type="h2" color="secondary" className={filterClasses.subtitle}>
            {t('titleFilterByCountry')}
          </Title>
          <Button
            color="accent"
            onClick={() => toggleSelectAllCountries()}
            pointer
          >
            {!allCountriesSelected
              ? t('buttonSelectAllLabel')
              : t('buttonDeselectAllLabel')}
          </Button>
        </div>
        <Separator />
        <div className={filterClasses.body}>
          <TreeCheckbox
            checkedStatus={checkedTreeState}
            items={countries}
            label={(item) => (item.code ? t(item.code) : item.name)}
            onCheckboxChange={handleChangeSelectedCenterOrCountry}
          />
        </div>
      </div>
      <div className={filterClasses.actions}>
        <Button
          color="accent"
          variant="flat"
          onClick={() => handleApplyFilters()}
          pointer
        >
          {t('btnApplyFilters')}
        </Button>
      </div>
      {children}
    </div>
  );
}

MainFilters.propTypes = {
  children: PropTypes.node,
};
